import React, {useMemo} from 'react';

import {Select} from '@kit';

import {humanEnumeration} from '../../../../components/utils';
import {useCharacter} from '../character-context';
import {abilityChoiceError} from '../model/ability-scores';
import {choiceLimit, isChoiceComplete} from '../model/choice-limits';
import {choiceOptions, optionLabel} from '../model/choice-options';
import type {ChoiceInstance} from '../model/feature-tree';
import type {ChooseType} from '../model/types';

import css from './master.module.css';

const TYPE_LABELS: Record<ChooseType, string> = {
	ability: 'Характеристика',
	skill: 'Навык',
	expertise: 'Экспертность',
	tool: 'Инструменты',
	instrument: 'Музыкальный инструмент',
	gamingSet: 'Игровой набор',
	language: 'Язык',
	weapon: 'Оружие',
	spell: 'Заклинание',
	feat: 'Черта',
	feature: 'Вариант',
	subClass: 'Подкласс',
	proficiency: 'Навык или инструмент',
	option: 'Вариант',
	item: 'Предмет',
};

/** заголовок группы заклинаний по кругу в выпадающем списке */
const spellLevelGroup = (level: number | undefined) =>
	level === undefined ? undefined : level === 0 ? 'Фокусы' : `${level} круг`;

type Props = {
	choice: ChoiceInstance;
	/** выбранное до начала смены: новых вариантов можно добавить не больше changeLimit */
	originalSelection?: string[];
};

export const ChoiceField: React.FC<Props> = ({choice, originalSelection}) => {
	const {catalog, character, setChoice} = useCharacter();
	const {group, selected} = choice;
	const options = useMemo(() => choiceOptions(choice, catalog, character), [choice, catalog, character]);
	const limit = choiceLimit(choice, character);
	const isMultiple = group.multiple || (limit ?? Infinity) > 1;
	const label = group.label || TYPE_LABELS[group.type];
	const error = abilityChoiceError(choice, character.abilityOverflows);

	if (choice.locked || (options.length === 1 && group.default === options[0].key)) {
		return (
			<div className={css.lockedChoice}>
				<span className={css.choiceLabel}>{label}:</span>{' '}
				{humanEnumeration(selected.map((key) => optionLabel(group, key, catalog)))}
				<NestedChoices choice={choice} />
			</div>
		);
	}

	const isLimitReached = limit !== undefined && selected.length >= limit;
	const changeLimit = originalSelection && group.changeLimit;
	const addedCount = originalSelection ? selected.filter((key) => !originalSelection.includes(key)).length : 0;
	const isChangeLimitReached = changeLimit !== undefined && addedCount >= changeLimit;
	const spellLevel = (key: string) => (group.type === 'spell' ? catalog.getSpell(key)?.level : undefined);
	const sortedOptions =
		group.type === 'spell'
			? [...options].sort(
					(first, second) =>
						(spellLevel(first.key) ?? 0) - (spellLevel(second.key) ?? 0) ||
						first.label.localeCompare(second.label, 'ru'),
			  )
			: options;
	const items = sortedOptions.map((option) => ({
		value: option.key,
		label: option.label,
		group: spellLevelGroup(spellLevel(option.key)),
		disabled:
			(isMultiple && isLimitReached && !selected.includes(option.key)) ||
			(isChangeLimitReached && !selected.includes(option.key) && !originalSelection?.includes(option.key)),
	}));
	const changeText = changeLimit !== undefined ? `Заменено ${addedCount} из ${changeLimit}` : undefined;
	const bottomText =
		[isMultiple && limit !== undefined ? `Выбрано ${selected.length} из ${limit}` : undefined, changeText]
			.filter(Boolean)
			.join(' · ') || undefined;

	const handleChange = (values: (string | number)[]) => {
		const keys = values.map(String);
		setChoice(choice.key, limit === undefined ? keys : keys.slice(0, limit));
	};

	return (
		<div className={css.choice}>
			<Select
				label={label}
				items={items}
				value={isMultiple ? selected : selected[0] ?? null}
				multiple={isMultiple}
				placeholder="Выберите…"
				error={error}
				highlighted={!isChoiceComplete(choice, catalog, character)}
				bottomText={bottomText}
				onChange={handleChange}
			/>
			<NestedChoices choice={choice} />
		</div>
	);
};

const NestedChoices: React.FC<Props> = ({choice}) =>
	choice.nested.length ? (
		<div className={css.nestedChoices}>
			{choice.nested.map((nestedChoice) => (
				<ChoiceField key={nestedChoice.key} choice={nestedChoice} />
			))}
		</div>
	) : null;
